/**
 * Subscription Settings — superadmin red panel (Phase 3).
 * Edit the GlobalBlockPolicy: pricing, periods, block toggles, storage defaults.
 */

import { useCallback, useEffect, useState } from 'react'
import { superadminAPI } from '../../services/api'
import {
  HiOutlineCog6Tooth,
  HiOutlineCheck,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2'

export default function SubscriptionSettingsPage() {
  const [policy, setPolicy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superadminAPI.getBlockPolicy()
      setPolicy(res?.data?.data ?? res?.data ?? null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { reload() }, [reload])

  const setField = (k, v) => setPolicy(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    setSavedMsg('')
    try {
      const res = await superadminAPI.updateBlockPolicy(policy)
      if (res?.success) {
        setSavedMsg('Settings saved successfully.')
        setTimeout(() => setSavedMsg(''), 3000)
      } else {
        setSavedMsg('Save failed.')
      }
    } catch (e) {
      setSavedMsg('Save failed.')
    } finally {
      setSaving(false)
    }
  }

  if (loading || !policy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <HiOutlineCog6Tooth className="w-6 h-6 text-danger" /> Subscription Settings
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          System-wide pricing, periods, block policy, and storage routing.
          Changes affect new signups and renewals — existing customers stay on their current terms.
        </p>
      </div>

      {savedMsg && (
        <div className={`p-3 rounded ${savedMsg.includes('failed') ? 'bg-danger-soft text-red-800' : 'bg-success-soft text-green-800'} flex items-center gap-2 text-sm`}>
          <HiOutlineCheck className="w-5 h-5" /> {savedMsg}
        </div>
      )}

      {/* Pricing */}
      <Section title="Pricing">
        <Grid>
          <Number label="Annual price ($) — new signups" value={policy.annual_price_usd}
            onChange={v => setField('annual_price_usd', v)} />
          <Number label="Renewal price ($) — existing customers" value={policy.renewal_price_usd}
            onChange={v => setField('renewal_price_usd', v)} />
          <Number label="Renewal price change notice (days)"
            value={policy.renewal_price_change_notice_days}
            onChange={v => setField('renewal_price_change_notice_days', v)} />
        </Grid>
      </Section>

      {/* Periods */}
      <Section title="Trial & grace periods">
        <Grid>
          <Number label="Trial duration (days)" value={policy.trial_days}
            onChange={v => setField('trial_days', parseInt(v) || 0)} />
          <Number label="Grace period (days)" value={policy.grace_period_days}
            onChange={v => setField('grace_period_days', parseInt(v) || 0)} />
        </Grid>
      </Section>

      {/* Block policy */}
      <Section title="Block policy" subtitle="What's blocked when a subscription is past_due/blocked">
        <div className="space-y-2">
          <Toggle label="Block uploads (documents)" value={policy.block_uploads}
            onChange={v => setField('block_uploads', v)} />
          <Toggle label="Block voice message uploads" value={policy.block_voice_messages}
            onChange={v => setField('block_voice_messages', v)} />
          <Toggle label="Block text-message file attachments" value={policy.block_text_messages_with_files}
            onChange={v => setField('block_text_messages_with_files', v)} />
          <Toggle label="Block downloads" value={policy.block_downloads}
            onChange={v => setField('block_downloads', v)}
            warning="Blocking downloads may have legal implications for case files." />
          <Toggle label="Block viewing" value={policy.block_viewing}
            onChange={v => setField('block_viewing', v)}
            warning="Blocking viewing is very harsh — recommend keeping off." />
          <Toggle label="Block voice message streaming" value={policy.block_streaming}
            onChange={v => setField('block_streaming', v)} />
        </div>
      </Section>

      {/* Payment gateway toggle */}
      <Section title="Payment gateway" subtitle="Switch between mock testing console and real Stripe.">
        <Select label="Active payment gateway"
          value={policy.payment_gateway || 'mock'}
          onChange={v => setField('payment_gateway', v)}
          options={[
            ['mock', 'Mock (testing — no real charges)'],
            ['stripe', 'Stripe (production — real card charges)'],
          ]} />
        <p className="text-xs text-gray-500 mt-2">
          When using <strong>Mock</strong>, customers see the testing checkout page and admin can simulate any event from the{' '}
          <a href="/superadmin/mock-payments" className="text-danger underline">Mock Payments Console</a>.{' '}
          Only switch to <strong>Stripe</strong> after STRIPE_SECRET_KEY / STRIPE_WEBHOOK_SECRET env vars are configured on the server.
        </p>
      </Section>

      {/* Storage */}
      <Section title="Storage backend defaults">
        <Grid>
          <Select label="Default backend for new signups"
            value={policy.default_storage_backend}
            onChange={v => setField('default_storage_backend', v)}
            options={[
              ['google_drive', 'Google Drive'],
              ['r2', 'Cloudflare R2 (OVACS-managed)'],
              ['local', 'Local disk (legacy)'],
            ]} />
          <Select label="System fallback backend"
            value={policy.system_storage_fallback_backend}
            onChange={v => setField('system_storage_fallback_backend', v)}
            options={[
              ['r2', 'Cloudflare R2'],
              ['local', 'Local disk'],
            ]} />
        </Grid>
        <Toggle label="Enable system-wide storage fallback"
          value={policy.system_storage_fallback_enabled}
          onChange={v => setField('system_storage_fallback_enabled', v)}
          warning="Toggle this ON during a Google Drive outage to route all new uploads to the fallback backend." />
      </Section>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  )
}

const Section = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-lg shadow-sm p-5">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5 mb-3">{subtitle}</p>}
    <div className={subtitle ? '' : 'mt-3'}>{children}</div>
  </div>
)

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
)

const Number = ({ label, value, onChange }) => (
  <label className="block">
    <span className="text-xs uppercase text-gray-500 tracking-wide block mb-1">{label}</span>
    <input
      type="number"
      step="any"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
    />
  </label>
)

const Select = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="text-xs uppercase text-gray-500 tracking-wide block mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
      {options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  </label>
)

const Toggle = ({ label, value, onChange, warning }) => (
  <div className="flex items-start gap-3 py-2">
    <label className="inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 text-danger border-gray-300 rounded" />
      <span className="ml-2 text-sm text-gray-900">{label}</span>
    </label>
    {warning && (
      <span className="text-xs text-warning flex items-center gap-1 ml-2">
        <HiOutlineExclamationTriangle className="w-4 h-4" /> {warning}
      </span>
    )}
  </div>
)
